import { useRef, useState } from 'react'
import { isValidImageFile } from '../lib/utils'
import PhotoAnnotationModal from './PhotoAnnotationModal'
import Icon from './Icon'

export default function PhotoUploadMulti({ onPhotosSelected, disabled = false, maxPhotos = 5 }) {
  const [photos, setPhotos] = useState([])
  const [error, setError] = useState(null)
  const [annotatingIndex, setAnnotatingIndex] = useState(null)
  const fileInputRef = useRef(null)

  const handleFileSelect = async (file) => {
    setError(null)

    if (photos.length >= maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed`)
      return
    }

    const validation = isValidImageFile(file)
    if (!validation.valid) {
      setError(validation.error)
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const newPhoto = {
        id: Date.now(),
        dataUrl: e.target.result,
        file: file,
        annotated: null,
      }
      const updatedPhotos = [...photos, newPhoto]
      setPhotos(updatedPhotos)
      onPhotosSelected(updatedPhotos)
      setAnnotatingIndex(updatedPhotos.length - 1)
    }
    reader.readAsDataURL(file)
  }

  const handleInputChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleRemovePhoto = (id) => {
    const updatedPhotos = photos.filter((p) => p.id !== id)
    setPhotos(updatedPhotos)
    onPhotosSelected(updatedPhotos)
  }

  const handleAnnotationSave = (annotatedDataUrl) => {
    const updatedPhotos = [...photos]
    updatedPhotos[annotatingIndex].annotated = annotatedDataUrl
    setPhotos(updatedPhotos)
    onPhotosSelected(updatedPhotos)
    setAnnotatingIndex(null)
  }

  const canAddMore = photos.length < maxPhotos

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {canAddMore && (
        <div
          onClick={() => !disabled && fileInputRef.current?.click()}
          className={`border-2 border-dashed border-border rounded-xl px-6 py-8 text-center transition-all cursor-pointer ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-accent/60 hover:bg-accent/5'
          }`}
        >
          <div className="inline-flex p-4 rounded-xl bg-surface-high mb-4">
            <Icon name="add_photo_alternate" className="text-accent text-3xl" />
          </div>
          <h3 className="text-label-md font-semibold text-text-primary mb-1">Add Photo</h3>
          <p className="text-label-sm text-text-secondary mb-3">
            {photos.length}/{maxPhotos} photos ({maxPhotos - photos.length} remaining)
          </p>
          <p className="text-label-sm text-text-muted">Tap to take or choose from gallery</p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleInputChange}
            disabled={disabled}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Photos Grid */}
      {photos.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-label-md font-semibold text-text-primary">
            Photos ({photos.length}/{maxPhotos})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <div key={photo.id} className="relative rounded-lg overflow-hidden bg-surface-high group">
                <img
                  src={photo.annotated || photo.dataUrl}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setAnnotatingIndex(index)
                    }}
                    className="p-2 rounded-full bg-accent hover:bg-accent-light transition"
                    title="Annotate"
                  >
                    <Icon name="edit" className="text-surface-dark text-lg" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemovePhoto(photo.id)
                    }}
                    className="p-2 rounded-full bg-status-urgent hover:bg-red-600 transition"
                    title="Remove"
                  >
                    <Icon name="close" className="text-white text-lg" />
                  </button>
                </div>
                {photo.annotated && (
                  <div className="absolute top-2 right-2 bg-status-resolved/90 rounded-full p-1">
                    <Icon name="check_circle" filled className="text-white text-sm" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && <p className="text-status-urgent text-label-sm">{error}</p>}

      {/* Annotation Modal */}
      {annotatingIndex !== null && (
        <PhotoAnnotationModal
          imageDataUrl={photos[annotatingIndex].dataUrl}
          onSave={handleAnnotationSave}
          onClose={() => setAnnotatingIndex(null)}
        />
      )}

      {/* Info */}
      {photos.length > 0 && (
        <div className="flex items-start gap-2 bg-status-resolved/10 border border-status-resolved/40 rounded-lg p-3">
          <Icon name="info" className="text-status-resolved text-lg shrink-0 mt-0.5" />
          <p className="text-label-sm text-text-secondary">
            Click on any photo to annotate and remove faces for privacy.
          </p>
        </div>
      )}
    </div>
  )
}
